import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Aviso, Brasao } from "../../components/Layout";
import { api, entrarComoCandidato, fmt, type ErroApi } from "../../lib/api";

type EstadoNavegacao = { cpf?: string; dataNascimento?: string } | null;

/**
 * Define a primeira senha da conta, logo depois do cadastro: chega aqui com
 * cpf/dataNascimento já preenchidos pela navegação, e eles ficam travados —
 * são o dado de identidade que o servidor confere (item 8.9), não algo pra
 * editar aqui. Quem não tem esses dois pela navegação (ex.: recarregou a
 * página) é mandado pro "Primeiro acesso", que pede os dois à mão.
 */
export function CriarSenha() {
  const { state } = useLocation() as { state: EstadoNavegacao };
  const cpf = state?.cpf ?? "";
  const dataNascimento = state?.dataNascimento ?? "";
  const [senha, setSenha] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const navigate = useNavigate();

  if (!cpf || !dataNascimento) {
    return (
      <div className="tema-candidato tc-entrada">
        <div className="tc-entrada-corpo">
          <div className="tc-entrada-caixa">
            <div className="linha" style={{ justifyContent: "center", marginBottom: 8 }}>
              <Brasao tamanho={32} />
            </div>
            <div className="tc-cartao pilha">
              <Aviso tipo="info" titulo="Confirme seu CPF e data de nascimento">
                Para criar a senha fora do fluxo de cadastro, confirme os dois dados na tela de
                "Primeiro acesso".
              </Aviso>
              <Link to="/primeiro-acesso" className="tc-btn tc-btn-primario tc-btn-largo">
                Ir para Primeiro acesso
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  async function enviar(ev: FormEvent) {
    ev.preventDefault();
    setErro(null);

    if (senha.length < 8) {
      setErro("A senha deve ter pelo menos 8 caracteres.");
      return;
    }
    if (senha !== confirmacao) {
      setErro("As senhas não conferem.");
      return;
    }

    setEnviando(true);
    try {
      await api.post("/api/publico/candidatos/senha", {
        cpf: cpf.replace(/\D/g, ""), dataNascimento, senhaNova: senha,
      });
      await entrarComoCandidato(cpf.replace(/\D/g, ""), senha);
      navigate("/area");
    } catch (e) {
      const err = e as ErroApi;
      setErro(err.motivos[0] ?? err.mensagem);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="tema-candidato tc-entrada">
      <div className="tc-entrada-corpo">
        <div className="tc-entrada-caixa">
          <div className="linha" style={{ justifyContent: "center", marginBottom: 8 }}>
            <Brasao tamanho={32} />
          </div>

          <form onSubmit={enviar} className="tc-cartao" noValidate>
            <h1 className="tc-titulo">Criar senha</h1>
            <p className="tc-ajuda">
              É com CPF e essa senha que você entra na sua conta pra se inscrever e
              acompanhar as suas inscrições.
            </p>

            {erro && <div className="tc-erro" role="alert">{erro}</div>}

            <div className="tc-campo">
              <label htmlFor="cpf">CPF</label>
              <input id="cpf" value={fmt.cpf(cpf)} disabled readOnly />
            </div>

            <div className="tc-campo">
              <label htmlFor="nascimento">Data de nascimento</label>
              <input id="nascimento" type="date" value={dataNascimento} disabled readOnly />
            </div>

            <div className="tc-campo">
              <label htmlFor="senha">Nova senha</label>
              <input id="senha" type="password" value={senha} required minLength={8}
                     autoComplete="new-password" onChange={(e) => setSenha(e.target.value)} />
            </div>

            <div className="tc-campo">
              <label htmlFor="confirmacao">Confirme a senha</label>
              <input id="confirmacao" type="password" value={confirmacao} required
                     autoComplete="new-password" onChange={(e) => setConfirmacao(e.target.value)} />
            </div>

            <button type="submit" className="tc-btn tc-btn-primario tc-btn-largo" disabled={enviando}>
              {enviando ? "Criando..." : "Criar senha e entrar"}
            </button>
          </form>

          <div className="tc-links">
            <Link to="/entrar">Já tenho senha</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
